import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import RtlsMap from "./components/rtlsMap";
import Modal from "./components/Modal";
import ActivityHistory from "./components/modalContent/moduleMetricsModalComponents/activityHistory";
import ComparisonTool from "./components/modalContent/moduleMetricsModalComponents/comparisonTool";
import MOCK_MODULES from "../pages/mockData/mockModuleData";
import { useIsMobile } from "./hooks/useIsMobile";
import DisabledTooltip from "./components/disabledToolTip";
import "./moduleMetrics.css";
import "../animation.css";

// ─── Function button definitions ─────────────────────────────────────────────
// First 4 are RTLS stubs, last 2 open modals.

const FUNCTION_BUTTONS = [
  {
    id: "heatmap",
    label: "Heatmap",
    subtitle: "RTLS API",
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <circle cx="12" cy="12" r="3" />
        <circle cx="12" cy="12" r="7" opacity="0.5" />
        <circle cx="12" cy="12" r="10" opacity="0.25" />
      </svg>
    ),
  },
  {
    id: "spaghetti",
    label: "Spaghetti Map",
    subtitle: "RTLS API",
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path d="M3 12c0-4 2-8 6-8s6 4 6 8-2 8-6 8" />
        <path d="M15 4c4 0 6 4 6 8s-2 8-6 8" />
      </svg>
    ),
  },
  {
    id: "workflow",
    label: "Workflow Map",
    subtitle: "RTLS API",
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <rect x="3" y="3" width="5" height="5" rx="1" />
        <rect x="16" y="3" width="5" height="5" rx="1" />
        <rect x="3" y="16" width="5" height="5" rx="1" />
        <rect x="16" y="16" width="5" height="5" rx="1" />
        <line x1="8" y1="5.5" x2="16" y2="5.5" />
        <line x1="5.5" y1="8" x2="5.5" y2="16" />
        <line x1="18.5" y1="8" x2="18.5" y2="16" />
        <line x1="8" y1="18.5" x2="16" y2="18.5" />
      </svg>
    ),
  },
  {
    id: "playback",
    label: "Playback",
    subtitle: "RTLS API",
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <polygon points="5 3 19 12 5 21 5 3" />
      </svg>
    ),
  },
  {
    id: "activity",
    label: "Activity History",
    subtitle: "SQL / D365",
    modal: true,
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
        <polyline points="10 9 9 9 8 9" />
      </svg>
    ),
  },
  {
    id: "comparison",
    label: "Comparison Tool",
    subtitle: "SQL / D365",
    modal: true,
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <line x1="18" y1="20" x2="18" y2="10" />
        <line x1="12" y1="20" x2="12" y2="4" />
        <line x1="6" y1="20" x2="6" y2="14" />
      </svg>
    ),
  },
];

// ─── Main page component ─────────────────────────────────────────────────────

const ModuleMetricsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedModule, setSelectedModule] = useState(null);
  const [activeModal, setActiveModal] = useState(null); // 'activity' | 'comparison'

  // On mobile (≤480px): show only the 2 modal-backed buttons (Activity History,
  // Comparison Tool). The 4 RTLS-API buttons are hidden — they depend on the
  // embedded map which is also hidden on mobile (see conditional render below).
  const isMobile = useIsMobile();
  const visibleButtons = isMobile
    ? FUNCTION_BUTTONS.filter((btn) => btn.modal)
    : FUNCTION_BUTTONS;

  // Handle URL parameter for auto-loading a module
  useEffect(() => {
    const workOrderParam = searchParams.get("workOrder");
    if (workOrderParam && !selectedModule) {
      // Find module by workOrder
      const module = MOCK_MODULES.find((m) => m.workOrder === workOrderParam);
      if (module) {
        setSearch(workOrderParam);
        setSelectedModule(module);
        // Clear URL params after loading to avoid re-triggering
        setSearchParams({});
      }
    }
  }, [searchParams, selectedModule, setSearchParams]);

  // ── CHANGED: Return top 10 modules when query is empty (default list),
  //            otherwise filter normally across workOrder, itemNumber, batchNumber.
  const filteredModules = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return MOCK_MODULES.slice(0, 10);
    return MOCK_MODULES.filter(
      (m) =>
        m.workOrder.toLowerCase().includes(q) ||
        m.itemNumber.toLowerCase().includes(q) ||
        m.batchNumber.toLowerCase().includes(q),
    );
  }, [search]);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setDropdownOpen(true);
  };

  const handleSelectModule = (mod) => {
    setSelectedModule(mod);
    setSearch(mod.workOrder);
    setDropdownOpen(false);
  };

  const handleFunctionBtn = useCallback(
    (btnId) => {
      if (!selectedModule) return;
      if (btnId === "activity") {
        setActiveModal(btnId);
        return;
      }
      // RTLS API stubs — wire up when SDK is available
      // console.log(
      //   `RTLS function triggered: ${btnId} for`,
      //   selectedModule.workOrder,
      // );
    },
    [selectedModule],
  );
  // Build the RIoT search query for the selected module.
  // null  → no filter call (map shows all objects).
  // query → hides every object whose name doesn't match the work order.
  const moduleFilterQuery = useMemo(() => {
    if (!selectedModule) return null;
    // Use the work order as the object name in RIoT.
    // Adjust the field key ("name") to match what your RIoT instance indexes.
    return `name:"${selectedModule.workOrder}"`;
  }, [selectedModule]);

  const closeModal = useCallback(() => setActiveModal(null), []);

  const completionColor = selectedModule
    ? selectedModule.completionRate >= 80
      ? "var(--mm-green)"
      : selectedModule.completionRate >= 50
        ? "var(--mm-amber)"
        : "var(--mm-red)"
    : "var(--mm-red)";

  return (
    <div className="mm-page page-enter">
      <div className="mm-page-header">
        <h1>Module Metrics</h1>
        <p className="mm-page-subtitle">
          Search and inspect individual module data and RTLS position
        </p>
      </div>

      <div className="mm-layout">
        {/* ── Left: RTLS Map (desktop only) ──────────────────────
            On mobile the map is hidden here and available via the
            dedicated /rtls-map route in the hamburger menu.       */}
        {!isMobile && (
          <div className="mm-map-col">
            <RtlsMap isHomePage={false} filterQuery={moduleFilterQuery} />
          </div>
        )}

        {/* ── Right: panel with internal scroll ──────────────── */}
        <div className="mm-right-col">
          {/* Search & selection — elevated z-index so dropdown clears cards below */}
          <div
            className="mm-card mm-search-card stagger-item"
            style={{ "--i": 0 }}
          >
            <div className="mm-search-wrap">
              <div className="mm-search-icon">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </div>
              <input
                className="mm-search-input"
                type="text"
                placeholder="Search by WO, Item Number, or Batch Number..."
                value={search}
                onChange={handleSearchChange}
                // ── CHANGED: Always open the dropdown on focus so the top-10
                //            default list is immediately visible.
                onFocus={() => setDropdownOpen(true)}
                onBlur={() => setTimeout(() => setDropdownOpen(false), 150)}
                aria-label="Search modules"
              />
              {search && (
                <button
                  className="mm-search-clear"
                  onClick={() => {
                    setSearch("");
                    setDropdownOpen(false);
                    setSelectedModule(null);
                  }}
                  aria-label="Clear search"
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              )}
            </div>

            {dropdownOpen && filteredModules.length > 0 && (
              <ul className="mm-dropdown" role="listbox">
                {/* ── CHANGED: Show a subtle header when displaying the default
                              top-10 list (i.e. no query has been typed yet). */}
                {!search.trim()}
                {filteredModules.map((mod) => (
                  <li
                    key={mod.workOrder}
                    className="mm-dropdown-item"
                    onMouseDown={() => handleSelectModule(mod)}
                    role="option"
                  >
                    <span className="mm-dropdown-wo">{mod.workOrder}</span>
                    <span className="mm-dropdown-meta">
                      {mod.itemNumber} &bull; {mod.batchNumber}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            {dropdownOpen && search.trim() && filteredModules.length === 0 && (
              <div className="mm-dropdown mm-dropdown--empty">
                No modules match your search.
              </div>
            )}
          </div>

          {/* Empty state */}
          {!selectedModule && (
            <div className="mm-empty-state stagger-item" style={{ "--i": 1 }}>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
              >
                <rect x="3" y="3" width="18" height="18" rx="3" />
                <path d="M9 9h6M9 12h6M9 15h4" />
              </svg>
              <p>Search for a module to view details</p>
              <span>
                Enter a work order, item number, or batch number above
              </span>
            </div>
          )}

          {selectedModule && (
            <>
              {/* Section 1: Actions */}
              <div className="mm-card stagger-item" style={{ "--i": 1 }}>
                <h2 className="mm-section-title">Actions</h2>
                <div className="mm-fn-grid">
                  {visibleButtons.map((btn) => (
                    <DisabledTooltip
                      key={btn.id}
                      disabled={btn.id === "comparison" ? true : !btn.modal}
                    >
                      <button
                        className={`mm-fn-btn${btn.modal ? " mm-fn-btn--modal" : ""}`}
                        onClick={() => handleFunctionBtn(btn.id)}
                        title={btn.subtitle}
                      >
                        <div className="mm-fn-btn-icon">{btn.icon}</div>
                        <div className="mm-fn-btn-text">
                          <span className="mm-fn-btn-label">{btn.label}</span>
                          <span className="mm-fn-btn-sub">{btn.subtitle}</span>
                        </div>
                      </button>
                    </DisabledTooltip>
                  ))}
                </div>
              </div>

              {/* Section 2: Module Information */}
              <div className="mm-card stagger-item" style={{ "--i": 2 }}>
                <h2 className="mm-section-title">Module Information</h2>
                <div className="mm-info-grid">
                  <div className="mm-info-item">
                    <span className="mm-info-label">Work Order</span>
                    <span className="mm-info-value">
                      {selectedModule.workOrder}
                    </span>
                  </div>
                  <div className="mm-info-item">
                    <span className="mm-info-label">Item Number</span>
                    <span className="mm-info-value">
                      {selectedModule.itemNumber}
                    </span>
                  </div>
                  <div className="mm-info-item">
                    <span className="mm-info-label">Batch Number</span>
                    <span className="mm-info-value">
                      {selectedModule.batchNumber}
                    </span>
                  </div>
                  <div className="mm-info-item">
                    <span className="mm-info-label">Online Date</span>
                    <span className="mm-info-value">
                      {selectedModule.onlineDate}
                    </span>
                  </div>
                  <div className="mm-info-item">
                    <span className="mm-info-label">Square Footage</span>
                    <span className="mm-info-value">
                      {selectedModule.sqft.toLocaleString()} sqft
                    </span>
                  </div>
                  <div className="mm-info-item">
                    <span className="mm-info-label">Property</span>
                    <span className="mm-info-value">
                      {selectedModule.property}
                    </span>
                  </div>
                </div>
              </div>

              {/* Section 3: Module Metrics */}
              <div className="mm-card stagger-item" style={{ "--i": 3 }}>
                <h2 className="mm-section-title">Module Metrics</h2>
                <div className="mm-metrics-grid">
                  <div className="mm-metric-tile">
                    <span className="mm-metric-label">Current Station</span>
                    <span className="mm-metric-value">
                      {selectedModule.currentStation}
                    </span>
                  </div>

                  <div className="mm-metric-tile">
                    <span className="mm-metric-label">Jobs Redone</span>
                    <span
                      className={`mm-metric-value ${selectedModule.jobsRedone > 0 ? "mm-metric-value--alert" : "mm-metric-value--ok"}`}
                    >
                      {selectedModule.jobsRedone}
                    </span>
                  </div>

                  <div className="mm-metric-tile">
                    <span className="mm-metric-label">Completion Rate</span>
                    <span
                      className="mm-metric-value"
                      style={{ color: completionColor }}
                    >
                      {selectedModule.completionRate}%
                    </span>
                  </div>

                  <div className="mm-metric-tile">
                    <span className="mm-metric-label">Time in Station</span>
                    <span className="mm-metric-value">
                      {selectedModule.timeInStation}
                    </span>
                  </div>

                  <div className="mm-metric-tile">
                    <span className="mm-metric-label">Time on Line</span>
                    <span className="mm-metric-value">
                      {selectedModule.timeOnLine}
                    </span>
                  </div>

                  <div className="mm-metric-tile">
                    <span className="mm-metric-label">Est. Completion</span>
                    <span className="mm-metric-value">
                      {selectedModule.estCompletionDate}
                    </span>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Activity History modal — content extracted to activityHistory.js */}
      <Modal
        isOpen={activeModal === "activity"}
        onClose={closeModal}
        title="Activity History"
        size="large"
      >
        {selectedModule && <ActivityHistory module={selectedModule} />}
      </Modal>

      {/* Comparison Tool modal — content extracted to comparisonTool.js
      <Modal
        isOpen={activeModal === "comparison"}
        onClose={closeModal}
        title="Comparison Tool"
        size="large"
      >
        {selectedModule && <ComparisonTool module={selectedModule} />}
      </Modal> */}
    </div>
  );
};

export default ModuleMetricsPage;
